import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map } from 'rxjs';
import { select } from '@ngneat/elf';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faBell,
  faHandWave,
  faHeadphonesSimple,
  faHeadphonesSlash,
  faMicrophone,
  faMicrophoneSlash,
  faSliders,
  faVideo,
  faVideoSlash,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AppStore } from '../../../state/app.repository';
import { AudioMuteStates, UserSettingsStore } from '../../../state/user-settings.repository';
import { MediaConfigStore } from '../../../state/media-config.repository';
import { PermissionStore } from '../../../state/permissions.repository';

import { AppService } from '../../services/app.service';
import { DeviceService } from '../../services/device.service';
import {
  camErrorMessage,
  LocalStreamService,
  screenShareSupported,
} from '../../services/local-stream.service';
import { MediaPermissionsService } from '../../services/media-permissions.service';
import { OdinService } from '../../services/odin.service';
import { UserService } from '../../services/user.service';
import { NotificationService } from '../../services/notification.service';
import { UserActivationService } from '../../services/user-activation.service';

import { GeneralSettings, NotificationSettings } from '../../../features/settings';
import { CameraPreview } from './camera-preview/camera-preview';
import { ScreenShareButton } from './screen-share-button/screen-share-button';
import { SplitToggleButton } from '../../../shared/components/ui/buttons/split-toggle-button/split-toggle-button';
import { SquareIconBtn } from '../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { SelectOption } from '../../../shared/components/form-fields/select-field/select-field';
import { VolumeIcon } from '../../../shared/components/volume-icon/volume-icon';
import { getPercentage } from '../../../shared/helpers/number';

type ExpansionContent = 'notifications' | 'audio-video-settings';

@Component({
  selector: 'andor-dock',
  standalone: true,
  imports: [
    TranslatePipe,
    FaDuotoneIconComponent,
    GeneralSettings,
    NotificationSettings,
    SplitToggleButton,
    SquareIconBtn,
    VolumeIcon,
    ScreenShareButton,
  ],
  templateUrl: './dock.html',
  styleUrl: './dock.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dock {
  // Icons
  protected readonly faMicrophone = faMicrophone;
  protected readonly faMicrophoneSlash = faMicrophoneSlash;
  protected readonly faHeadphones = faHeadphonesSimple;
  protected readonly faHeadphonesSlash = faHeadphonesSlash;
  protected readonly faVideo = faVideo;
  protected readonly faVideoSlash = faVideoSlash;
  protected readonly faHandWave = faHandWave;
  protected readonly faBell = faBell;
  protected readonly faSliders = faSliders;
  protected readonly faXmark = faXmark;

  // Reactive state
  protected readonly content = signal<ExpansionContent>('audio-video-settings');
  protected readonly bellRinging = signal(false);
  protected readonly cameraStarting = signal(false);
  protected readonly volumeLevel = signal(0);
  private _bellTimer?: ReturnType<typeof setTimeout>;
  // How long the bell keeps ringing after an unseen notification arrives. The
  // CSS animation loops; this timer is what actually stops it.
  private static readonly BELL_RING_MS = 10_000;
  private static readonly VOLUME_MIN_DBFS = -90;
  private static readonly VOLUME_MAX_DBFS = -1;

  protected readonly micActive = toSignal(this.userSettingsStore.micActive$(), {
    initialValue: false,
  });
  protected readonly speakersActive = toSignal(this.userSettingsStore.speakersActive$(), {
    initialValue: false,
  });
  protected readonly camStream = toSignal(this.localStream.camStream$);
  protected readonly screenShareSupported = screenShareSupported;

  protected readonly audioInputDevices = toSignal(this.deviceService.audioInputDeviceOptions$, {
    initialValue: [] as SelectOption[],
  });
  protected readonly activeAudioInputDevice = toSignal(
    this.deviceService.activeAudioInputDeviceOption$
  );
  protected readonly videoDevices = toSignal(this.deviceService.videoDeviceOptions$, {
    initialValue: [] as SelectOption[],
  });
  protected readonly selectedVideoDevice = toSignal(this.deviceService.selectedVideoDevice$);

  protected readonly noAudioPermission = toSignal(
    combineLatest([
      this.permissionStore.useMicrophone$,
      this.userSettingsStore.store$.pipe(select((s) => s.talkPermissionGranted)),
    ]).pipe(map(([useMicrophone, talkGranted]) => !useMicrophone && !talkGranted)),
    { initialValue: false }
  );
  protected readonly noVideoPermission = toSignal(
    this.permissionStore.useCamera$.pipe(map((useCamera) => !useCamera)),
    { initialValue: false }
  );

  protected readonly panelExpanded = toSignal(this.appStore.panelSettingsExpanded$, {
    initialValue: false,
  });

  protected readonly hasRaisedHand = computed(
    () => !!this.userService.ownUser()?.data.hasRaisedHand
  );

  // "Disabled" means "cannot turn ON": while the mic/cam is live, the button
  // must stay clickable so the user can always mute — a talk-granted user
  // loses `talkPermissionGranted` the moment they unmute (one-shot grant), so
  // gating on the permission alone would leave them stuck transmitting.
  protected readonly micDisabled = computed(
    () => this.odinService.isAudioStarting() || (this.noAudioPermission() && !this.micActive())
  );
  protected readonly camDisabled = computed(
    () => this.cameraStarting() || (this.noVideoPermission() && !this.camStream())
  );
  protected readonly showMicPermissionTooltip = computed(
    () => this.noAudioPermission() && !this.micActive()
  );
  // Browser mic access blocked: the toggle opens the permission guide instead.
  protected readonly micBlocked = computed(
    () => this.permissions.microphoneAccess() === 'denied' && !this.micActive()
  );
  protected readonly showVideoPermissionTooltip = computed(
    () => this.noVideoPermission() && !this.camStream()
  );

  constructor(
    public appStore: AppStore,
    public appService: AppService,
    public userService: UserService,
    public deviceService: DeviceService,
    public localStream: LocalStreamService,
    public permissions: MediaPermissionsService,
    public userSettingsStore: UserSettingsStore,
    public permissionStore: PermissionStore,
    public notificationService: NotificationService,
    protected odinService: OdinService,
    protected userActivation: UserActivationService,
    private _mediaConfigStore: MediaConfigStore,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _translate: TranslateService
  ) {
    inject(DestroyRef).onDestroy(() => {
      if (this._bellTimer) clearTimeout(this._bellTimer);
    });
    effect(() => {
      // Only new actionable notifications start/restart the animation.
      this.notificationService.notificationAlertSequence();
      const unseen = untracked(() => this.notificationService.notificationsUnseenAlerting());
      if (this._bellTimer) clearTimeout(this._bellTimer);
      if (unseen > 0) {
        this.bellRinging.set(true);
        this._bellTimer = setTimeout(() => this.bellRinging.set(false), Dock.BELL_RING_MS);
      } else {
        // Unseen count cleared (e.g. the user opened notifications) — stop ringing.
        this.bellRinging.set(false);
      }
    });
    effect(() => {
      if (this.notificationService.notificationsUnseenAlerting() === 0) {
        if (this._bellTimer) clearTimeout(this._bellTimer);
        this.bellRinging.set(false);
      }
    });

    // Mirror the prep-area volume indicator: throttle PowerLevel events to 100ms,
    // map dBFS into a 0–100 percentage, and reset to 0 whenever the mic is muted.
    inject(DestroyRef).onDestroy(this.odinService.holdLevelPolling(100));
    let lastPowerLevelUpdate = 0;
    this.odinService.audioInput$.pipe(takeUntilDestroyed()).subscribe((audioInput) => {
      if (audioInput) {
        audioInput.addEventListener('PowerLevel', (event) => {
          const now = performance.now();
          if (now - lastPowerLevelUpdate < 100) return;
          lastPowerLevelUpdate = now;
          this.volumeLevel.set(
            getPercentage(
              event.payload.media.powerLevel,
              Dock.VOLUME_MIN_DBFS,
              Dock.VOLUME_MAX_DBFS
            )
          );
        });
      } else {
        this.volumeLevel.set(0);
      }
    });

    this.userSettingsStore.mutedState$.pipe(takeUntilDestroyed()).subscribe((state) => {
      if (state !== AudioMuteStates.none) {
        this.volumeLevel.set(0);
      }
    });
  }

  // ─── Mic ─────────────────────────────────────────
  protected async toggleMic() {
    if (this.micActive()) {
      this.stopMic();
    } else {
      await this.startMic();
    }
  }

  private async startMic() {
    if (this.noAudioPermission()) return;
    // Blocked in the browser, which won't prompt again: keep the toggle off.
    // requestMic() opens the permission guide — after re-probing on Firefox,
    // where a lifted temporary block can leave the state stale at 'denied'.
    if (
      this.permissions.microphoneAccess() === 'denied' &&
      (await this.permissions.requestMic()) !== 'granted'
    ) {
      return;
    }
    this.permissions.approveMediaAccess();
    // A running recording gates the unmute behind the consent prompt; the
    // prompt's "I understand" completes the unmute.
    if (this.odinService.requestRecordingConsent()) return;
    this.userSettingsStore.update({ mutedState: AudioMuteStates.none });
  }

  private stopMic() {
    this.userSettingsStore.update({ mutedState: AudioMuteStates.mic });
  }

  protected onAudioInputSelected(device: SelectOption) {
    this.deviceService.selectAudioInputDeviceById(device.id);
  }

  // ─── Speakers ────────────────────────────────────
  protected toggleSpeakers() {
    const state = this.userSettingsStore.value.mutedState;
    if (this.speakersActive()) {
      // mute speakers
      let newState = state;
      if (state === AudioMuteStates.mic) {
        newState = AudioMuteStates.speakersKeepMic;
      } else if (state === AudioMuteStates.none) {
        newState = AudioMuteStates.speakers;
      }
      this.userSettingsStore.update({ mutedState: newState });
    } else {
      // unmute speakers
      if (state === AudioMuteStates.speakersKeepMic) {
        this.userSettingsStore.update({ mutedState: AudioMuteStates.mic });
      } else if (state === AudioMuteStates.speakers) {
        this.userSettingsStore.update({ mutedState: AudioMuteStates.none });
      }
    }
  }

  // ─── Camera ──────────────────────────────────────
  protected async toggleCam() {
    if (this.camStream() && this.permissions.cameraAccess() === 'granted') {
      this.stopCam();
    } else {
      await this.startCam();
    }
  }

  private async startCam() {
    if (this.noVideoPermission()) return;
    this.cameraStarting.set(true);
    if (!this._mediaConfigStore.value.showCameraPreview) {
      try {
        await this.odinService.startCam();
      } catch (e) {
        console.error(e);
        // No preview dialog to surface the failure in — use a snackbar.
        this._snackBar.open(
          this._translate.instant(camErrorMessage(this.localStream.camError())),
          undefined,
          { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top' }
        );
      }
      this.cameraStarting.set(false);
      return;
    }
    const ref = this._dialog.open(CameraPreview, { panelClass: 'camera-preview-dialog' });
    ref.afterOpened().subscribe(() => this.cameraStarting.set(false));
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this.localStream.stopCamStream('camera preview dialog');
      }
    });
  }

  private stopCam() {
    this.localStream.stopCamStream('dock');
    this._dialog.closeAll();
  }

  protected onVideoDeviceSelected(device: SelectOption) {
    this.deviceService.selectVideoDevice(device.id);
  }

  // ─── Expansion (notifications / settings) ───────
  protected toggleNotifications() {
    if (this.content() === 'notifications' && this.panelExpanded()) {
      this.appStore.togglePanelSettings();
    } else {
      this.content.set('notifications');
      if (!this.appStore.value.panelSettingsExpanded) {
        this.appStore.togglePanelSettings();
      }
    }
  }

  protected toggleSettings() {
    if (this.content() === 'audio-video-settings' && this.panelExpanded()) {
      this.appStore.togglePanelSettings();
    } else {
      this.content.set('audio-video-settings');
      if (!this.appStore.value.panelSettingsExpanded) {
        this.appStore.togglePanelSettings();
      }
    }
  }

  protected closeExpansion() {
    if (this.panelExpanded()) {
      this.appStore.togglePanelSettings();
    }
  }

  protected toggleRaiseHand() {
    this.userService.toggleRaiseHand();
  }
}
